"use client";

import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Braces, KeyRound, Radio } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Button,
  CODE,
  DARK_BAND,
  Eyebrow,
  SectionShell,
  Terminal,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { gsap, NOT_REDUCED, useGSAP, useMaskedReveal } from "@/components/sections/kit/motion";
import { roadmapItem } from "@/lib/roadmap";
import { routePath } from "@/lib/routes";

interface Payload {
  label: string;
  link: string;
  country: string;
  device: string;
  isQr: boolean;
}

/** Three sample frames from GET /v1/events/stream — one `click` event type,
 *  same schema, different values (payload per
 *  backend services/request_log_consumer.py: link_id, country, device,
 *  referrer, utm_*, is_qr, rule_matched, timestamp). That sameness is the
 *  point of the section, so the frame skeleton never changes shape. */
const PAYLOADS: Payload[] = [
  { label: "click · qr", link: "lnk_8h2f0qZ3Tk1mR4vXw9aBcQ", country: "DE", device: "mobile", isQr: true },
  { label: "click · desktop", link: "lnk_2xw11pGh6Rt0Yc3Mv9LsJg", country: "FR", device: "desktop", isQr: false },
  { label: "click · tablet", link: "lnk_5Tq0bLm2Wc8Rz1Kx7NvAeP", country: "US", device: "tablet", isQr: false },
];

const WEBHOOKS_ETA = roadmapItem("webhooks").promised;

/** Seconds a payload stays up before the row auto-advances. */
const DWELL = 4.2;

const FACTS: { Icon: LucideIcon; title: string; body: string }[] = [
  {
    Icon: Radio,
    title: "Live, as it happens",
    body: "Each human click on your API-created links arrives as one server-sent event.",
  },
  {
    Icon: Braces,
    title: "One schema",
    body: "link_id, country, device, referrer, utm_*, is_qr, rule_matched, timestamp.",
  },
  {
    Icon: KeyRound,
    title: "API-key auth",
    body: "The same bearer key as the REST API — create one in Settings → API keys.",
  },
];

/**
 * The universal connector — the honest answer to "my tool isn't listed".
 * A dark band pairing the claim with the literal thing that ships today: the
 * SSE click stream (API-created links only), the same frame for every click.
 * Outbound webhooks are roadmap (lib/roadmap.ts) and only named with their
 * date. The chip row cycles through three sample clicks and hands control to
 * the visitor on first click.
 *
 * Deliberately restrained (brief §4.4): this is the bridge to /developers,
 * which owns the API depth.
 */
export default function WebhooksConnector() {
  const [index, setIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const interactedRef = useRef(false);
  const killAutoplayRef = useRef<(() => void) | null>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });

  /** First real click stops the rotation for good — the visitor is driving. */
  const pick = (i: number) => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      killAutoplayRef.current?.();
    }
    setIndex(i);
  };

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        if (interactedRef.current) return;

        const tl = gsap.timeline({ paused: true, defaults: { immediateRender: false } });
        for (let i = 1; i <= PAYLOADS.length; i++) {
          tl.call(
            () => {
              if (document.hidden) return;
              flushSync(() => setIndex(i % PAYLOADS.length));
            },
            undefined,
            `+=${DWELL}`,
          );
        }

        let visible = false;
        let hold = 0;
        const replay = () => {
          window.clearTimeout(hold);
          hold = window.setTimeout(() => {
            if (visible && !interactedRef.current) tl.restart();
          }, 60);
        };
        tl.eventCallback("onComplete", replay);

        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              visible = e.isIntersecting;
              if (interactedRef.current) return;
              if (!e.isIntersecting) {
                tl.pause();
                return;
              }
              if (tl.progress() === 1) tl.restart();
              else tl.play();
            }),
          { threshold: 0.25 },
        );
        if (paneRef.current) io.observe(paneRef.current);

        killAutoplayRef.current = () => {
          window.clearTimeout(hold);
          tl.kill();
        };

        return () => {
          io.disconnect();
          window.clearTimeout(hold);
          tl.eventCallback("onComplete", null);
          tl.kill();
          killAutoplayRef.current = null;
        };
      });
    },
    { scope: paneRef },
  );

  const p = PAYLOADS[index];

  return (
    // md:pt-px/pb-px: the band butts the section edges so the row aligner puts
    // the first block exactly one free row inside the band's top line (the
    // AgentHero/Mcp band rhythm) — the shell's own py-[118px] pushes it two.
    <SectionShell
      id="webhooks"
      band="dark"
      decoAvoid
      className="scroll-mt-[var(--grid-cell,72px)] max-md:bg-[#14141c] md:pt-px md:pb-px"
    >
      <div ref={rootRef}>
        {/* Claim and proof, side by side — ONE block so both columns share a
            single clearing (a SectionHead would be its own block and stack). */}
        <div
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_500px] lg:gap-16"
        >
          <div ref={copyRef} className="min-w-0 max-w-[620px]">
            <div data-head-sub className="flex">
              <Eyebrow tone="light">Click stream</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em] text-white">
              Not listed? Stream it or script it.
            </h2>
            <p
              data-head-sub
              className="mt-[18px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-white/75"
            >
              Every click on your API-created links streams live from
              GET /v1/events/stream, and the REST API does the rest — your code
              decides what happens next. Outbound webhooks are on the roadmap
              for {WEBHOOKS_ETA}.
            </p>
            <div data-head-sub className="mt-7 flex">
              <Button href={routePath("developers")} variant="ghost-light">
                Read the API docs
              </Button>
            </div>
          </div>

          <div ref={paneRef} className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              {PAYLOADS.map((item, i) => (
                <button
                  key={item.label}
                  type="button"
                  aria-pressed={i === index}
                  onClick={() => pick(i)}
                  className={`rounded-full border px-3 py-[5px] font-jetbrains text-[11px] transition-colors ${
                    i === index
                      ? "border-white/25 bg-white/[0.15] text-white"
                      : "border-white/10 bg-white/[0.05] text-[#73738a] hover:text-white/80"
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <span className="ml-auto self-center font-jetbrains text-[10.5px] text-[#73738a]">
                one schema, every click
              </span>
            </div>

            <Terminal path="GET https://api.rii.link/v1/events/stream">
              {/* `whitespace-pre` + a scroll container: the frame is always
                  exactly four lines, so a value swap can never reflow the
                  pane's height. */}
              <div className="overflow-x-auto">
                <pre className="m-0 whitespace-pre font-jetbrains text-[12.5px] leading-[1.85]">
                  <span className={CODE.key}>event:</span> click
                  {"\n"}
                  <span className={CODE.key}>data:</span> {"{ "}
                  <span className={CODE.key}>&quot;link_id&quot;</span>:{" "}
                  <span className={CODE.string}>&quot;{p.link}&quot;</span>,{"\n"}
                  {"  "}
                  <span className={CODE.key}>&quot;country&quot;</span>:{" "}
                  <span className={CODE.string}>&quot;{p.country}&quot;</span>,{" "}
                  <span className={CODE.key}>&quot;device&quot;</span>:{" "}
                  <span className={CODE.string}>&quot;{p.device}&quot;</span>,{"\n"}
                  {"  "}
                  <span className={CODE.key}>&quot;is_qr&quot;</span>:{" "}
                  <span className={CODE.number}>{String(p.isQr)}</span>, … {"}"}
                </pre>
              </div>
            </Terminal>
          </div>
        </div>

        {/* Three facts — the "you're covered" evidence, nothing more. */}
        <div
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="grid gap-4 max-md:mt-10 sm:grid-cols-3"
        >
          {FACTS.map(({ Icon, title, body }) => (
            <div key={title} className="flex items-start gap-3.5">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] border border-white/[0.16] text-brand-400">
                <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <div>
                <div className="text-[14px] font-bold text-white">{title}</div>
                <p className="mt-[5px] text-[12.5px] leading-[1.55] text-[#a6a6b8]">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
